import type { Control } from "react-hook-form"

import { Card,CardContent,CardHeader,CardTitle } from "@/components/ui/card"
import { FormControl,FormField,FormItem,FormLabel,FormMessage } from "@/components/ui/form"
import { NumericInput } from "@/components/ui/numeric-input"
import { MultiSelect } from "@/components/ui/multi-select"
import type { Shop } from "@/types"

import type { SaleConfigFormValues } from "./sale-config-utils"

interface PurchasingLimitsCardProps {
  control: Control<SaleConfigFormValues>
  show: boolean
  onToggleShow: () => void
}

interface TargetShopCardProps {
  shops: Shop[]
  saleConfigId?: string | null
  targetShopIds: string[]
  onTargetShopIdsChange: (shopIds: string[]) => void
  /** In edit mode, the config's own shop — always selected, cannot be removed. */
  lockedShopId?: string | null
}

export const PurchasingLimitsCard = ({ control, show, onToggleShow }: PurchasingLimitsCardProps) => {
  return (
    <>
      <div className="flex items-center justify-start">
        <button
          type="button"
          onClick={onToggleShow}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Advanced options
        </button>
      </div>
      {show && (
        <Card>
          <CardHeader className="px-4 py-1.5">
            <CardTitle className="text-base">Purchasing Limits</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 px-4 pb-4">
            <FormField
              control={control}
              name="minQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Min</FormLabel>
                  <FormControl>
                    <NumericInput 
                      value={String(field.value ?? "")} 
                      onChange={(val) => field.onChange(parseFloat(val) || 0)} 
                      allowDecimals={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="maxQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max</FormLabel>
                  <FormControl>
                    <NumericInput 
                      value={String(field.value ?? "")} 
                      onChange={(val) => field.onChange(parseFloat(val) || 0)} 
                      allowDecimals={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="quantityStep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Step</FormLabel>
                  <FormControl>
                    <NumericInput 
                      value={String(field.value ?? "")} 
                      onChange={(val) => field.onChange(parseFloat(val) || 0)} 
                      allowDecimals={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="defaultQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default</FormLabel>
                  <FormControl>
                    <NumericInput 
                      value={String(field.value ?? "")} 
                      onChange={(val) => field.onChange(parseFloat(val) || 0)} 
                      allowDecimals={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      )}
    </>
  )
}

export const TargetShopCard = ({
  shops,
  saleConfigId,
  targetShopIds,
  onTargetShopIdsChange,
  lockedShopId,
}: TargetShopCardProps) => {
  if (shops.length <= 1) return null

  // The only real constraint is the API's: a config must keep at least one
  // shop. Lock whichever shop is last rather than always pinning the one you
  // happened to open the panel from — an owner may legitimately move a config
  // to another store.
  const lockedValues = targetShopIds.length <= 1 ? targetShopIds : []
  const leavingCurrentShop = !!saleConfigId && !!lockedShopId && !targetShopIds.includes(lockedShopId)
  const currentShopName = shops.find((s) => s.id === lockedShopId)?.shopName ?? 'this shop'

  return (
    <Card className="gap-0 py-0 mb-6">
      <CardHeader className="p-4 pb-2 space-y-0">
        <CardTitle className="text-base">Target Shop</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <FormItem className="space-y-1">
          <FormLabel className="sr-only">Select Shops</FormLabel>
          <MultiSelect
            options={shops.map((s) => ({ label: s.shopName, value: s.id }))}
            selected={targetShopIds}
            onChange={onTargetShopIdsChange}
            lockedValues={lockedValues}
            placeholder="Select shops..."
            className="min-h-8 text-sm"
          />
          <div className="text-[0.7rem] text-muted-foreground">
            {saleConfigId
              ? 'Shops this configuration is available in. A configuration must serve at least one shop.'
              : 'Create specific sale configurations for one or more shops.'}
          </div>
          {leavingCurrentShop && (
            <div className="text-[0.7rem] font-medium text-amber-600 dark:text-amber-500">
              Saving will remove this configuration from {currentShopName}, so it will disappear from this list.
            </div>
          )}
        </FormItem>
      </CardContent>
    </Card>
  )
}
